import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  compose,
  lifecycle,
  withState,
  withHandlers,
  withProps,
} from "recompose";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import { Box, Grid, Button } from "@material-ui/core";
import ConfirmButton from "../../../../components/ConfirmButton/ConfirmButton";
import CustomExpansionPanel from "../../../../components/CustomExpansionPanel/CustomExpansionPanel";
import Prompt from "../../../../components/Prompt/Prompt";
import CustomSwitchWithTitle from "../../../../components/SwitchWidthTitle/CustomSwitchWithTitle";
import styles from "./Webhooks.module.scss";
import {
  WEBHOOK_TYPES,
  WEBHOOK_SNACKBAR_MESSAGES,
  URL_REQUIRED_WHEN_WEBHOOK_ENABLED,
  ENABLE_WEBHOOK_DETAILS,
  SAVE,
  GO_AWAY_MESSAGE,
  WEBHOOK_ENABLE_INFO_LABELS,
  WEBHOOKS,
  CANCEL,
  WEBHOOK_LABEL,
} from "../../../../constants/strings";
import {
  AccountDetails,
  WebhookDetails,
  WebhookToggles,
  SuspendedAccess,
} from "./components";
import TestWebhook from "./components/TestWebhook";
import WebhookTermsAndConditions from "./components/WebhookTermsAndConditions";
import { getUserBusinessId } from "../../../../features/user/user.selectors";
import {
  getWebhookConfiguration,
  getSuspendedSecrets,
  findWebhookConfigurationByType,
} from "../../../../features/webhooks/webhook.selectors";
import {
  fetchWebhookConfig,
  generateWebhookSecretAction,
  revokeWebhookSecretAction,
  clearWebhookConfiguration,
  updateWebhookConfigurationAction,
  addSuspendedSecret,
  fetchSuspendedSecretsAction,
  setWebhookConfigurationRow,
} from "../../../../features/webhooks/webhook.actions";
import { isCurrentUserDpdAdmin } from "../../../../features/user/user.selectors";
import { showSnackbar } from "../../../../features/snackbar/snackbar.actions";
import { isValidUrl } from "../../../../utils/commonValidation";
import {
  hasUnsavedWebhookChanges,
  hasDirtyWebhookConfiguration,
  isAllGroup,
  getWebhookConfigByType,
  getMergedWebhookConfig,
  getEnabledNotificationTypeCodes,
  getEnabledEventTypeCodes,
} from "./webhooks.helpers";

const CANCEL_BUTTON_STYLE = {
  marginRight: "16px",
  height: "43px",
};

const SAVE_BUTTON_STYLE = {
  width: "72px",
  height: "43px",
};

const getWebhookConfigForType = (config, type) =>
  findWebhookConfigurationByType(config, type) || {};

const getMergedWebhookConfigForType = (config, dirtyConfig, type) =>
  getMergedWebhookConfig(
    getWebhookConfigForType(config, type),
    getWebhookConfigByType(dirtyConfig, type)
  );

const updateDirtyWebhookTypeConfig = (
  setDirtyConfig,
  config,
  type,
  updater
) => {
  setDirtyConfig((prevDirty) => ({
    ...(prevDirty || {}),
    [type]: updater(getMergedWebhookConfigForType(config, prevDirty, type)),
  }));
};

const removeDirtyWebhookTypeConfig = (setDirtyConfig, type) => {
  setDirtyConfig((previousDirtyConfig) => {
    if (!previousDirtyConfig) {
      return null;
    }

    const nextDirtyConfig = { ...previousDirtyConfig };
    delete nextDirtyConfig[type];

    return Object.keys(nextDirtyConfig).length > 0 ? nextDirtyConfig : null;
  });
};

const syncDirtyWebhookTypeConfig = (setDirtyConfig, type, nextConfig) => {
  setDirtyConfig((previousDirtyConfig) => {
    if (previousDirtyConfig?.[type]) {
      return previousDirtyConfig;
    }

    return {
      ...(previousDirtyConfig || {}),
      [type]: nextConfig,
    };
  });
};

const getWebhookErrorMessage = (error, fallbackMessage) =>
  error?.error?.message || error?.message || fallbackMessage;

const Webhooks = ({
  isDpdAdmin,
  config,
  dirtyConfig,
  accountNumber,
  currentBusinessId,
  isFormDirty,
  handleAccountSearch,
  handleEnableWebhookDetailsToggle,
  handleWebhookUrlChange,
  handleWebhookToggle,
  handleSecretInUseToggle,
  handleGenerateSecret,
  handleRevokeSecret,
  handleSaveChanges,
  handleCancel,
  setDirtyConfig,
  fetchWebhookConfig,
}) => {
  return (
    <PageContainer>
      <Prompt when={isFormDirty} message={GO_AWAY_MESSAGE} />
      <AccountDetails
        businessId={currentBusinessId}
        accountNumber={accountNumber}
        isDpdAdmin={isDpdAdmin}
        onAccountSearch={handleAccountSearch}
      />

      {(!isDpdAdmin || accountNumber) && (
        <>
          {config?.webhookConfiguration?.map((webhookTypeConfig, index) => {
            const { type } = webhookTypeConfig;
            const dirtyWebhookTypeConfig = getMergedWebhookConfigForType(
              config,
              dirtyConfig,
              type
            );

            const { webhookIsEnabled, url, secrets } = dirtyWebhookTypeConfig;

            const label = WEBHOOK_LABEL[type];
            const toggles = dirtyWebhookTypeConfig?.[label.toLowerCase()] ?? {};

            return (
              <CustomExpansionPanel key={type} title={`${WEBHOOKS} - ${label}`}>
                <Grid container spacing={3}>
                  <CustomSwitchWithTitle
                    input={{
                      checked: webhookIsEnabled ?? false,
                      onChange: (event) =>
                        handleEnableWebhookDetailsToggle(
                          type,
                          event.target.checked
                        ),
                    }}
                    label={ENABLE_WEBHOOK_DETAILS}
                    infoLabel={WEBHOOK_ENABLE_INFO_LABELS[type.toUpperCase()]}
                  />

                  <WebhookTermsAndConditions />

                  {webhookIsEnabled && (
                    <>
                      <WebhookDetails
                        webhookIsEnabled={webhookIsEnabled ?? false}
                        webhookUrl={url || ""}
                        webhookSecrets={secrets || []}
                        webhookType={type}
                        onGenerateSecret={handleGenerateSecret}
                        onRevokeSecret={handleRevokeSecret}
                        onWebhookUrlChange={(value) =>
                          handleWebhookUrlChange(type, value)
                        }
                        onSecretInUseToggle={(secretId, inUse) =>
                          handleSecretInUseToggle(type, secretId, inUse)
                        }
                      />

                      {Object.keys(toggles).length > 0 && (
                        <WebhookToggles
                          label={label}
                          webhookType={type}
                          toggles={toggles}
                          handleWebhookToggle={(category, code, enabled) =>
                            handleWebhookToggle(type, category, code, enabled)
                          }
                        />
                      )}

                      {isDpdAdmin && accountNumber && (
                        <SuspendedAccess
                          webhookType={type}
                          accountNumber={accountNumber}
                          businessId={currentBusinessId}
                          webhookConfig={dirtyWebhookTypeConfig}
                          setDirtyConfig={(next) =>
                            setDirtyConfig((p) => ({ ...p, [type]: next }))
                          }
                          setConfig={() => {
                            fetchWebhookConfig(
                              accountNumber,
                              currentBusinessId
                            );
                          }}
                        />
                      )}
                    </>
                  )}

                  <Grid container>
                    <Box className={styles.buttonContainer}>
                      {isDpdAdmin && accountNumber && (
                        <Button
                          color="secondary"
                          onClick={() => handleCancel(type)}
                          style={CANCEL_BUTTON_STYLE}
                        >
                          {CANCEL}
                        </Button>
                      )}
                      <ConfirmButton
                        text={SAVE}
                        onClick={() => handleSaveChanges(type)}
                        disabled={
                          !hasUnsavedWebhookChanges(
                            getWebhookConfigForType(config, type),
                            getWebhookConfigByType(dirtyConfig, type)
                          )
                        }
                        style={SAVE_BUTTON_STYLE}
                      />
                    </Box>
                  </Grid>

                  {webhookIsEnabled && (
                    <TestWebhook
                      webhookType={type}
                      dirtyConfig={dirtyWebhookTypeConfig}
                      config={webhookTypeConfig}
                      accountNumber={accountNumber}
                      businessUnit={currentBusinessId}
                    />
                  )}
                </Grid>
              </CustomExpansionPanel>
            );
          })}
        </>
      )}
    </PageContainer>
  );
};

Webhooks.propTypes = {
  isDpdAdmin: PropTypes.bool,
  config: PropTypes.object,
  dirtyConfig: PropTypes.object,
  accountNumber: PropTypes.string,
  currentBusinessId: PropTypes.number,
  isFormDirty: PropTypes.bool,
  handleAccountSearch: PropTypes.func,
  handleEnableWebhookDetailsToggle: PropTypes.func,
  handleWebhookUrlChange: PropTypes.func,
  handleWebhookToggle: PropTypes.func,
  handleSecretInUseToggle: PropTypes.func,
  handleGenerateSecret: PropTypes.func,
  handleRevokeSecret: PropTypes.func,
  handleSaveChanges: PropTypes.func,
  handleCancel: PropTypes.func,
  setDirtyConfig: PropTypes.func,
  fetchWebhookConfig: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => ({
  businessId: getUserBusinessId(state),
  accountNumber: ownProps.match.params.customerId,
  config: getWebhookConfiguration(state),
  suspendedSecrets: getSuspendedSecrets(state),
  isDpdAdmin: isCurrentUserDpdAdmin(state),
});

const mapDispatchToProps = (dispatch) => ({
  fetchWebhookConfig,
  updateWebhookConfigurationAction,
  addSuspendedSecret,
  fetchSuspendedSecretsAction,
  dispatch,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withState("dirtyConfig", "setDirtyConfig", null),
  withState("searchedAccountNumber", "setSearchedAccountNumber", null),
  withState("searchedBusinessId", "setSearchedBusinessId", null),
  withProps(
    ({
      isDpdAdmin,
      searchedAccountNumber,
      accountNumber: routeAccountNumber,
      searchedBusinessId,
      businessId,
    }) => ({
      accountNumber: isDpdAdmin ? searchedAccountNumber : routeAccountNumber,
      currentBusinessId: isDpdAdmin ? searchedBusinessId : businessId,
    })
  ),
  withProps(({ dirtyConfig, config }) => ({
    isFormDirty: hasDirtyWebhookConfiguration(config, dirtyConfig),
  })),
  withHandlers({
    handleAccountSearch:
      ({
        setSearchedAccountNumber,
        setSearchedBusinessId,
        dispatch,
        fetchWebhookConfig,
        setDirtyConfig,
      }) =>
      async (accountNumber, businessId) => {
        if (!accountNumber) return;

        const trimmedAccountNumber = accountNumber.trim();

        try {
          setSearchedAccountNumber(null);
          setSearchedBusinessId(null);

          dispatch(clearWebhookConfiguration());
          setDirtyConfig(null);

          const result = await dispatch(
            fetchWebhookConfig(trimmedAccountNumber, businessId)
          );

          if (result?.webhookConfiguration?.length === 0) {
            dispatch(
              showSnackbar({
                message: WEBHOOK_SNACKBAR_MESSAGES.NO_WEBHOOK_DATA_FOUND,
                variant: "error",
              })
            );
            return;
          }

          setSearchedAccountNumber(trimmedAccountNumber);
          setSearchedBusinessId(businessId);
        } catch (error) {
          dispatch(
            showSnackbar({
              message: getWebhookErrorMessage(
                error,
                WEBHOOK_SNACKBAR_MESSAGES.ERROR_SEARCHING_WEBHOOK_DATA
              ),
              variant: "error",
            })
          );
          setSearchedAccountNumber(null);
          setSearchedBusinessId(null);
        }
      },

    handleEnableWebhookDetailsToggle: (props) => (type, value) => {
      updateDirtyWebhookTypeConfig(
        props.setDirtyConfig,
        props.config,
        type,
        (currentConfig) => ({
          ...currentConfig,
          webhookIsEnabled: value,
        })
      );
    },

    handleWebhookUrlChange: (props) => (webhookType, value) => {
      updateDirtyWebhookTypeConfig(
        props.setDirtyConfig,
        props.config,
        webhookType,
        (currentConfig) => ({
          ...currentConfig,
          url: value,
        })
      );
    },

    handleWebhookToggle:
      (props) => (webhookType, categoryName, notificationCode, enabled) => {
        const isEvent = webhookType === WEBHOOK_TYPES.EVENT;
        const categoriesKey = isEvent ? "events" : "notifications";

        updateDirtyWebhookTypeConfig(
          props.setDirtyConfig,
          props.config,
          webhookType,
          (currentConfig) => {
            const nextConfig = {
              ...currentConfig,
              [categoriesKey]: {
                ...(currentConfig[categoriesKey] || {}),
              },
            };

            const category =
              categoryName && nextConfig[categoriesKey][categoryName] != null
                ? categoryName
                : Object.keys(nextConfig[categoriesKey]).find((cat) =>
                    (nextConfig[categoriesKey][cat] || []).some(
                      (item) => (item.code || item.group) === notificationCode
                    )
                  );

            if (!category) {
              return nextConfig;
            }

            const allEnabled =
              isEvent && isAllGroup(notificationCode) && enabled;
            const allDisabled =
              isEvent && isAllGroup(notificationCode) && !enabled;

            nextConfig[categoriesKey][category] = (
              nextConfig[categoriesKey][category] || []
            ).map((item) => {
              const itemKey = item.code || item.group;
              const toggled =
                itemKey === notificationCode ||
                (isAllGroup(notificationCode) && isAllGroup(itemKey));

              if (toggled) {
                return { ...item, webhookEnabled: enabled };
              }

              if (allEnabled) {
                return { ...item, webhookEnabled: false };
              }

              if (allDisabled && !isAllGroup(itemKey)) {
                return { ...item, webhookEnabled: false };
              }

              return item;
            });

            return nextConfig;
          }
        );
      },

    handleSecretInUseToggle: (props) => (webhookType, secretId, inUse) => {
      const currentConfig = getMergedWebhookConfigForType(
        props.config,
        props.dirtyConfig,
        webhookType
      );
      const secrets = currentConfig.secrets || [];

      if (!inUse) {
        const inUseCount = secrets.filter((secret) => secret.inUse).length;
        const targetSecret = secrets.find(
          (secret) => secret.secretId === secretId
        );

        if (targetSecret?.inUse && inUseCount === 1) {
          return;
        }
      }

      updateDirtyWebhookTypeConfig(
        props.setDirtyConfig,
        props.config,
        webhookType,
        (mergedConfig) => ({
          ...mergedConfig,
          secrets: (mergedConfig.secrets || []).map((secret) => {
            if (secret.secretId === secretId) {
              return { ...secret, inUse };
            }

            if (inUse) {
              return { ...secret, inUse: false };
            }

            return secret;
          }),
        })
      );
    },

    handleGenerateSecret:
      ({ dispatch, accountNumber, currentBusinessId, ...props }) =>
      async (webhookType) => {
        try {
          const generateSecretResult = await dispatch(
            generateWebhookSecretAction(
              accountNumber,
              webhookType,
              currentBusinessId
            )
          );

          const newSecret = generateSecretResult?.webhookSettings;
          const currentConfig = getMergedWebhookConfigForType(
            props.config,
            props.dirtyConfig,
            webhookType
          );

          if (!newSecret) {
            dispatch(
              showSnackbar({
                message: WEBHOOK_SNACKBAR_MESSAGES.SECRET_GENERATION_FAILED,
                variant: "error",
              })
            );
            return;
          }

          const validatedSecret = {
            secretId: newSecret.secretId,
            sharedSecret: newSecret.sharedSecret,
            createdAt: newSecret.createdAt,
            inUse: newSecret.inUse,
          };

          const updatedSecrets = (currentConfig.secrets || []).map(
            (secret) => ({
              ...secret,
              inUse: newSecret.inUse ? false : secret.inUse,
            })
          );

          props.setDirtyConfig((previousDirtyConfig) => ({
            ...(previousDirtyConfig || {}),
            [webhookType]: {
              ...currentConfig,
              secrets: [...updatedSecrets, validatedSecret],
            },
          }));

          dispatch(
            showSnackbar({
              message: WEBHOOK_SNACKBAR_MESSAGES.SECRET_GENERATED_SUCCESS,
              variant: "success",
            })
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: WEBHOOK_SNACKBAR_MESSAGES.SECRET_GENERATION_FAILED,
              variant: "error",
            })
          );
        }
      },

    handleRevokeSecret:
      ({
        dispatch,
        accountNumber,
        currentBusinessId,
        isDpdAdmin,
        addSuspendedSecret,
        ...props
      }) =>
      async (secretId, webhookType) => {
        try {
          await dispatch(
            revokeWebhookSecretAction(
              accountNumber,
              secretId,
              webhookType,
              currentBusinessId
            )
          );

          const mergedConfig = getMergedWebhookConfigForType(
            props.config,
            props.dirtyConfig,
            webhookType
          );
          const secrets = mergedConfig.secrets || [];
          const secretToRevoke = secrets.find(
            (secret) => secret.secretId === secretId
          );

          props.setDirtyConfig((previousDirtyConfig) => ({
            ...(previousDirtyConfig || {}),
            [webhookType]: {
              ...mergedConfig,
              secrets: secrets.filter((secret) => secret.secretId !== secretId),
            },
          }));

          if (isDpdAdmin && secretToRevoke) {
            dispatch(
              addSuspendedSecret(
                {
                  secretId: secretToRevoke.secretId,
                  sharedSecret: secretToRevoke.sharedSecret,
                  createdAt: secretToRevoke.createdAt,
                  suspendedAt: new Date().toISOString(),
                },
                webhookType
              )
            );
          }

          dispatch(
            showSnackbar({
              message: WEBHOOK_SNACKBAR_MESSAGES.SECRET_SUSPENDED,
              variant: "info",
            })
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: WEBHOOK_SNACKBAR_MESSAGES.SECRET_REVOKE_FAILED,
              variant: "error",
            })
          );
        }
      },

    handleCancel: (props) => (webhookType) => {
      const configRow = getWebhookConfigForType(props.config, webhookType);

      props.setDirtyConfig((previousDirtyConfig) => ({
        ...(previousDirtyConfig || {}),
        [webhookType]: JSON.parse(JSON.stringify(configRow)),
      }));
    },

    handleSaveChanges:
      ({
        dispatch,
        accountNumber,
        currentBusinessId,
        setDirtyConfig,
        ...props
      }) =>
      async (webhookType) => {
        const dirtyConfigRow = getWebhookConfigByType(
          props.dirtyConfig,
          webhookType
        );

        try {
          if (!dirtyConfigRow) {
            dispatch(
              showSnackbar({
                message: WEBHOOK_SNACKBAR_MESSAGES.PREFERENCES_UPDATE_FAILED,
                variant: "error",
              })
            );
            return;
          }

          const url = dirtyConfigRow.url;

          if (dirtyConfigRow.webhookIsEnabled && url && !isValidUrl(url)) {
            dispatch(
              showSnackbar({
                message: URL_REQUIRED_WHEN_WEBHOOK_ENABLED,
                variant: "error",
              })
            );
            return;
          }

          const enabledItems =
            webhookType === WEBHOOK_TYPES.NOTIFICATION
              ? getEnabledNotificationTypeCodes(dirtyConfigRow.notifications)
              : getEnabledEventTypeCodes(dirtyConfigRow.events);

          const secretStatus = (dirtyConfigRow.secrets || []).map((secret) => ({
            secretId: secret.secretId,
            inUse: secret.inUse || false,
          }));

          const savedConfigRow = {
            ...dirtyConfigRow,
            type: dirtyConfigRow.type || webhookType,
          };

          await dispatch(
            updateWebhookConfigurationAction(
              accountNumber,
              savedConfigRow.type,
              currentBusinessId,
              savedConfigRow.url || "",
              enabledItems,
              savedConfigRow.webhookIsEnabled ?? false,
              secretStatus
            )
          );

          dispatch(setWebhookConfigurationRow(savedConfigRow));
          removeDirtyWebhookTypeConfig(setDirtyConfig, webhookType);

          dispatch(
            showSnackbar({
              message: WEBHOOK_SNACKBAR_MESSAGES.PREFERENCES_UPDATED_SUCCESS,
              variant: "success",
            })
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: getWebhookErrorMessage(
                error,
                WEBHOOK_SNACKBAR_MESSAGES.PREFERENCES_UPDATE_FAILED
              ),
              variant: "error",
            })
          );
        }
      },
  }),
  lifecycle({
    componentDidMount() {
      const {
        accountNumber: routeAccountNumber,
        dispatch,
        fetchWebhookConfig,
        fetchSuspendedSecretsAction,
        isDpdAdmin,
        businessId,
        accountNumber,
        currentBusinessId,
      } = this.props;
      if (routeAccountNumber && !isDpdAdmin) {
        dispatch(clearWebhookConfiguration());
        dispatch(fetchWebhookConfig(routeAccountNumber, businessId));
      }
      if (isDpdAdmin && accountNumber && currentBusinessId) {
        dispatch(fetchSuspendedSecretsAction(accountNumber, currentBusinessId));
      }
    },

    componentDidUpdate(prevProps) {
      const {
        accountNumber: routeAccountNumber,
        config,
        dispatch,
        fetchWebhookConfig,
        fetchSuspendedSecretsAction,
        setDirtyConfig,
        isDpdAdmin,
        businessId,
        accountNumber,
        currentBusinessId,
      } = this.props;

      const { NOTIFICATION, EVENT } = WEBHOOK_TYPES;

      if (routeAccountNumber !== prevProps.accountNumber && !isDpdAdmin) {
        setDirtyConfig(null);
        dispatch(clearWebhookConfiguration());
        dispatch(fetchWebhookConfig(routeAccountNumber, businessId));
      }

      if (
        isDpdAdmin &&
        accountNumber &&
        currentBusinessId &&
        (accountNumber !== prevProps.accountNumber ||
          currentBusinessId !== prevProps.currentBusinessId)
      ) {
        dispatch(fetchSuspendedSecretsAction(accountNumber, currentBusinessId));
      }

      if (config !== prevProps.config) {
        let notifications = findWebhookConfigurationByType(
          config,
          NOTIFICATION
        );

        if (!notifications && config?.webhookConfiguration?.length > 0) {
          notifications = config.webhookConfiguration.find(
            (c) => c.type === null || c.type === NOTIFICATION
          );
        }

        if (notifications) {
          const n = {
            ...notifications,
            webhookIsEnabled: notifications.webhookIsEnabled ?? false,
          };
          syncDirtyWebhookTypeConfig(setDirtyConfig, NOTIFICATION, n);
        }

        const events = findWebhookConfigurationByType(config, EVENT);

        if (events) {
          const e = {
            ...events,
            webhookIsEnabled: events.webhookIsEnabled ?? false,
          };
          syncDirtyWebhookTypeConfig(setDirtyConfig, EVENT, e);
        }
      }
    },
  })
)(Webhooks);
